import { Decimal } from 'decimal.js';
import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { FunctionLimitMap } from '@/components/RightProvider/type';
import {
  getDistributionCode,
  getLimitConfig,
} from '@/components/UIComponents/CommonModal/service';
import { CouponList, getUserCoupon } from '@/components/UIComponents/UserAccount/service';
import baseConfig from '@/config';
import { getUserInfo } from '@/services/login';
import { fetchUsedSeats, queryRight } from '@/services/workspace';
import { getCookie } from '@/utils';
import { isProduction } from '@/utils/envHelper';
import { initTracker, setUserProfile } from '@/utils/eventlog/logAdapter';

import { masterFunctionMock, qaFunctionMock, VIPWEIGHT } from './const';
import { isUserCreatedWithin24Hours } from './util';

export enum CodeState {
  Enable = 0,
  Disable = 1,
}

// 分佣模式类型
// 1固定金额 2固定比例 3走商品
export enum Mode {
  FIXED_AMOUNT = 1,
  FIXED_RATIO = 2,
  PRODUCT_MODE = 3,
}

export type DistributionQueryType = 1 | 2; // 1查询自己的分销码，2查询绑定的分销码

// 单个商品的分佣配置
export interface ProceedsDiscountItem {
  commodityCode: string; // 商品编码
  proceedsRatio: number; // 分佣比例
  proceedsAmount: number; // 分佣金额
  proceedsMode: Mode; // 分佣模式
}

// 单个商品的优惠配置
export interface CommodityDiscountItem {
  commodityCode: string; // 商品编码
  discount: number; // 优惠比例
  discountAmount: number; // 优惠金额
  discountMode: Mode; // 优惠模式
}

export interface DistributionCodeData {
  type?: DistributionQueryType; // 查询类型
  distributionCode?: string; // 分销码
  proceedsRatio?: number; // 收益分成比例
  proceedsAmount?: number; // 收益分成金额
  proceedsMode?: Mode; // 分佣模式
  proceedsDiscount?: ProceedsDiscountItem[]; // 多商品分佣配置
  discount?: number; // 优惠比例
  discountAmount?: number; // 优惠金额
  discountMode?: Mode; // 优惠模式
  commodityDiscount?: CommodityDiscountItem[]; // 多商品优惠配置
  codeState?: CodeState; // 分销码状态

  inviteUrl?: string; // 邀请链接
  pattern?: string | number; // 分销码模式
}

/**
 * 身份的key（免费/初级/高级/企业）
 */
export type IDENTITYKEY = 'freeVip' | 'basicVip' | 'advancedVip' | 'enterpriseVip';
export enum VIPSOURCE {
  Purchase = 0,
  Vcm = 1,
  RedeemCode = 2,
}
export type UserVipSource = VIPSOURCE.Purchase | VIPSOURCE.Vcm | VIPSOURCE.RedeemCode;

// 会员类型
export type VIPType = {
  key: IDENTITYKEY;
  isExpire: boolean;
  weight: number;
  title: string;
  startTime: number;
  endTime: number;
  balance?: number;
  vipSource: UserVipSource;
};

interface PointsStatus {
  type: 1 | 2 | 3; // 1: 生效中, 2: 3天内过期, 3: 已经过期
  used: number; // 已使用积分
  left: number; // 剩余积分 ｜ 即将过期积分 ｜ 已过期积分
  startTime: number; // 积分开始时间戳
  endTime: number; // 积分结束时间戳
}

interface UserInfo {
  isRequestEnd: boolean;
  key: IDENTITYKEY;
  isExpire: boolean;
  weight: number;
  title: string;
  startTime: number;
  endTime: number;
  balance: number;
  recharged: boolean;
  isExpireUser: boolean;
  spaceStorage: {
    used: 0;
    total: 0;
  };
  cloudStorage: 0;
  // vip来源
  vipSource: UserVipSource;
  // 用户积分详情
  userPointDetails: Array<PointsStatus>;
  // 是否为24小时内的新会员
  isNewUser24h: boolean;
  userRegisterTime: number;
  // 用户注销时间
  exceptCancelTime: number;
}
interface UserSeat {
  total: number;
  used: number;
  available: number;
}
export interface CurrentVip extends UserInfo {
  avatarSeat: UserSeat;
  voiceSeat: UserSeat;
}

export type DistributionAllCouponInfo = {
  type: 'distribution_all';
  data: DistributionCodeData;
};

export type DistributionSpecificCouponInfo = {
  type: 'distribution_specific';
  data: DistributionCodeData;
};
export type CouponTicketInfo = {
  type: 'ticket';
  data: CouponList;
  availabled: CouponList | undefined;
  used: CouponList | undefined;
  extends?: {
    type: 'BASIC_VIP_UPGRADE' | 'NEW_USER_EXCLUSIVE';
  };
};
export type CouponInfo = (
  | DistributionAllCouponInfo
  | DistributionSpecificCouponInfo
  | CouponTicketInfo
) & {
  hasCoupon: boolean;
  isRequestEnd: boolean; // 是否请求结束
};

export type DiscountType = 'discount' | 'reduce' | 'none';

export interface CurrentDiscountInfo {
  hasCoupon: boolean; // 有没有折扣
  discountType: DiscountType; // 折扣类型：折扣 or 立减
  discountValue?: number; // 折扣数值：折扣比例 or 减免金额
  realPrice: number; // 最终价格
  originPrice: number; // 原始价格
  couponSourceType?: 'ticket' | 'distribution_specific' | 'distribution_all'; // 优惠来源
  couponDetail?: CouponInfo; // 具体券信息
}

interface HomeType {
  applyCouponDiscount(
    arg0: number,
    discountType: string,
    discountValue: number | undefined,
  ): number;
  getUserBenefit: (searchSeat?: boolean) => Promise<CurrentVip>;
  formatUserInfo: (data: any, info?: any) => UserInfo;
  formatSeat: (data: any, userInfo: any, type: 'avatar' | 'voice') => UserSeat;

  // 当前vip身份
  currentVip: CurrentVip;
  // 权益配置表
  functionLimitConfig: FunctionLimitMap;
  // 默认权益配置
  getDefaultFunctionLimitConfig: () => any;
  // 设置权益配置表
  setFunctionLimitConfig: (data: FunctionLimitMap) => void;
  // 获取权益配置表
  getFunctionLimitConfig: () => Promise<FunctionLimitMap>;

  // 用户的优惠券/折扣券信息
  couponInfo: CouponInfo;
  setCouponInfo: (couponInfo: CouponInfo) => void;
  getCouponInfo: () => Promise<CouponInfo>;
  fetchDistributionInfo: () => Promise<DistributionCodeData | undefined>;
  fetchCouponTicketInfo: () => Promise<
    CouponTicketInfo & { hasCoupon: boolean; isRequestEnd: boolean }
  >;

  getCurrentDiscountInfo: (
    originPrice: number,
    commodityCode: string,
    couponInfoData?: CouponInfo,
  ) => CurrentDiscountInfo;

  // 套餐接口loading
  purchaseLimitLoading: boolean;
  setPurchaseLimitLoading: (value: boolean) => void;

  // 新用户优惠券倒计时
  newUserCountDownTime: number;
  setNewUserCountDownTime: (time: number | ((prev: number) => number)) => void;

  showNewUserCountDownBaner: boolean;
  setShowNewUserCountDownBaner: (value: boolean) => void;

  userSalt: string | null;
  setUserSalt: (value: string | null) => void;

  getLoginState: () => boolean;
}

const UserStore = createWithEqualityFn<HomeType>()(
  devtools(
    (set, get) => ({
      // 当前vip身份
      currentVip: {
        // 请求身份的接口是否已结束，用于UI占位展示
        isRequestEnd: false,
        key: 'freeVip',
        title: 'free',
        isExpire: false,
        // 权重
        weight: VIPWEIGHT.freeVip,
        startTime: 0,
        endTime: 0,
        // 余额
        balance: 0,
        // 有没有充值过
        recharged: false,
        // 是否过期会员:假如是过期会员 则返回true
        isExpireUser: true,
        // 当前空间容量，单位是B（字节），1KB = 1024B，1MB = 1024KB
        spaceStorage: {
          used: 0,
          total: 0,
        },
        vipSource: VIPSOURCE.Purchase,
        // 云存储有效时长，单位是天
        cloudStorage: 0,
        // 克隆数字人席位
        avatarSeat: {
          total: 0,
          used: 0,
          available: 0,
        },
        // 克隆音色席位
        voiceSeat: {
          total: 0,
          used: 0,
          available: 0,
        },
        userPointDetails: [],
        isNewUser24h: false,
        userRegisterTime: 0,
        exceptCancelTime: 0,
      },
      formatUserInfo(res, info) {
        if (res?.data && res.data.length > 0) {
          // 当前剩余点数
          let rightInfo = res.data.find(
            (item: any) => item.key === 'clipDuration' || item.key === 'clipDuration\t',
          ) || {
            leftValue: 0,
          };
          // 当前空间容量
          let spaceInfo = res.data.find((item: any) => item.key === 'space') || {
            maxValue: 0,
            currentValue: 0,
          };
          // 当前云存储有效时长
          let cloudInfo = res.data.find(
            (item: any) => item.key === 'cloudStorageDays',
          ) || {
            leftValue: 0,
          };

          let isOldData = res.data.every((item: any) => {
            return !['freeVip', 'basicVip', 'advancedVip', 'enterpriseVip'].includes(
              item.key,
            );
          });
          if (isOldData) {
            // 旧数据，没有身份标识，统一为免费用户
            return {
              key: 'freeVip',
              title: 'free',
              isExpire: false,
              weight: VIPWEIGHT.freeVip,
              startTime: 0,
              endTime: 0,
              balance: 0,
              isRequestEnd: true,
              recharged: false,
              isExpireUser: true,
              vipSource: VIPSOURCE.Purchase,
              cloudStorage: cloudInfo.leftValue,
              spaceStorage: {
                total: spaceInfo.maxValue,
                used: spaceInfo.currentValue,
              },
              userPointDetails: [],
              isNewUser24h: false,
              userRegisterTime: 0,
              exceptCancelTime: 0,
            };
          } else {
            let curr = {
              key: 'freeVip' as IDENTITYKEY,
              title: 'free',
              isExpire: false,
              weight: VIPWEIGHT.freeVip,
              startTime: 0,
              endTime: 0,
              balance: 0,
              vipSource: VIPSOURCE.Purchase,
              isExpireUser: true,
            } as any;
            res.data.forEach((member: VIPType) => {
              // 未过期的大于免费用户的就是当前的
              if (!member.isExpire && VIPWEIGHT[member.key] > curr.weight) {
                curr = {
                  key: member.key,
                  title: member.title === 'week' ? 'twoweek' : member.title,
                  weight: VIPWEIGHT[member.key],
                  isExpire: member.isExpire,
                  startTime: member.startTime,
                  endTime: member.endTime,
                  vipSource: member.vipSource,
                };
              }
            });
            let vipData = res.data.filter((item: VIPType) =>
              ['basicVip', 'advancedVip', 'enterpriseVip'].includes(item.key),
            );
            curr.isRequestEnd = true;
            curr.isExpireUser = res.isExpireUser;
            curr.balance = Number((rightInfo.leftValue / 60).toFixed(1)) || 0;
            curr.cloudStorage = cloudInfo.leftValue;
            curr.spaceStorage = {
              total: spaceInfo.maxValue,
              used: spaceInfo.currentValue,
            };
            curr.recharged = res.data.some((item: any) =>
              ['basicVip', 'advancedVip', 'enterpriseVip'].includes(item.key),
            );
            curr.userPointDetails = res.userPointDetails || [];
            curr.isNewUser24h = isUserCreatedWithin24Hours(
              res.systemTime || 0,
              res.userRegisterTime || 0,
            );
            curr.userRegisterTime = res.userRegisterTime;
            if (info) {
              curr = {
                ...curr,
                ...info.data,
                exceptCancelTime: info.data.exceptCancelTime || 0,
              };
            }

            return curr;
          }
        } else {
          return get().currentVip;
        }
      },
      formatSeat(res: any, userInfo: any, type) {
        const seat = {
          available: 0,
          total: 0,
          used: 0,
        };
        let currentKey = type === 'avatar' ? 'maxDigitalClone' : 'maxToneClone';
        const target = userInfo.data.find((item: any) => item.key === currentKey);
        seat.total = target?.leftValue || 0;
        seat.used = res.data.count;
        seat.available = seat.total - res.data.count;
        return seat;
      },
      // 用户权益查询
      getUserBenefit: async (searchSeat = true) => {
        try {
          let result = get().currentVip;
          let arr = [queryRight(), getUserInfo(getCookie('user').accountType)];
          if (searchSeat) {
            arr.push(fetchUsedSeats({ type: 1 }), fetchUsedSeats({ type: 2 }));
          }
          const [user, info, avatar, voice] = await Promise.allSettled(arr);

          if (
            user.status === 'fulfilled' &&
            user.value?.success &&
            info.status === 'fulfilled' &&
            info.value?.success
          ) {
            const userData = get().formatUserInfo(user.value, info.value);
            result = { ...result, ...userData };

            if (searchSeat) {
              if (avatar.status === 'fulfilled' && avatar.value?.success) {
                const avatarSeat = get().formatSeat(avatar.value, user.value, 'avatar');
                result = { ...result, avatarSeat };
              }
              if (voice.status === 'fulfilled' && voice.value?.success) {
                const voiceSeat = get().formatSeat(voice.value, user.value, 'voice');
                result = { ...result, voiceSeat };
              }
            }
          }
          if (JSON.stringify(get().currentVip) !== JSON.stringify(result)) {
            console.group('Credit Info');
            console.dir(result);
            console.groupEnd();
            set({
              currentVip: result,
            });
          }

          setUserProfile(result);

          return result;
        } catch (error) {
          console.error('用户信息失败', error);
          return get().currentVip;
        }
      },
      setCurrentVip: (data: CurrentVip) => {
        set({ currentVip: data });
      },
      getFunctionLimitConfig: async (): Promise<FunctionLimitMap> => {
        let data;
        try {
          get().setPurchaseLimitLoading(true);
          let result;

          if (!getCookie('user').uid) {
            // get().setPurchaseLimitLoading(false);
            result = get().getDefaultFunctionLimitConfig();
          } else {
            result = await getLimitConfig();
          }

          if (result.success && result.data) {
            data = Object.fromEntries(
              Object.entries(result.data).filter(([key, value]) => {
                return !Array.isArray(value) || value.length > 0;
              }),
            ) as FunctionLimitMap;
          } else {
            data = get().getDefaultFunctionLimitConfig();
          }
        } catch (error) {
          data = get().getDefaultFunctionLimitConfig();
        } finally {
          get().setPurchaseLimitLoading(false);
        }

        set({
          functionLimitConfig: data,
        });
        return data;
      },
      functionLimitConfig: {} as FunctionLimitMap,

      getDefaultFunctionLimitConfig() {
        // return isCnArea
        //   ? ({
        //       freeVip: [],
        //       basicVip: [],
        //       advancedVip: [],
        //       enterpriseVip: [],
        //       commodity: [],
        //     } as unknown as FunctionLimitMap)
        //   : ({
        //       freeVip: [],
        //       basicVip: [],
        //       advancedVip: [],
        //       enterpriseVip: [],
        //       commodity: [],
        //     } as unknown as FunctionLimitMap);
        return isProduction ? masterFunctionMock : qaFunctionMock;
      },
      setFunctionLimitConfig: (data: FunctionLimitMap) => {
        set({ functionLimitConfig: data });
      },
      purchaseLimitLoading: false,
      setPurchaseLimitLoading: (value: boolean) => {
        set({ purchaseLimitLoading: value });
      },

      couponInfo: {
        type: 'ticket',
        data: [],
        availabled: undefined,
        used: undefined,
        hasCoupon: false,
        isRequestEnd: false,
      },
      setCouponInfo: (couponInfo: CouponInfo) => {
        set({ couponInfo });
      },
      // 获取分销码信息
      fetchDistributionInfo: async () => {
        const { data, success } = await getDistributionCode<DistributionCodeData>(2);
        if (
          !success ||
          !data ||
          (data as DistributionCodeData)?.codeState === CodeState.Disable ||
          (data as DistributionCodeData).pattern === 1
        )
          return undefined;

        return data as DistributionCodeData;
      },
      //获取优惠券
      fetchCouponTicketInfo: async () => {
        const { code, data } = await getUserCoupon<CouponList>();
        if (code !== 200 || !data) {
          return {
            type: 'ticket',
            data: [],
            availabled: undefined,
            used: undefined,
            hasCoupon: false,
            isRequestEnd: true,
          };
        }
        const couponList = data as CouponList;

        const hasNewUserExclusive = couponList.filter((item) => item.mode === 1);
        const hasBasicVipExclusive = couponList.filter((item) => item.mode === 2);
        if (hasBasicVipExclusive.length) {
          // 基础版升级的差价优惠
          if (hasBasicVipExclusive.length) {
            const availabled = hasBasicVipExclusive.map((item) => ({
              ...item,
              applicableGoods:
                (item.applicableGoods as unknown as string).split(',') || [],
            }));
            return {
              type: 'ticket',
              data: couponList,
              availabled,
              used: undefined,
              hasCoupon: true,
              isRequestEnd: true,
              extends: {
                type: 'BASIC_VIP_UPGRADE',
              },
            };
          }
        }
        if (hasNewUserExclusive.length) {
          // 新用户专享的优惠券，其次走可用的优惠券
          const availabled = hasNewUserExclusive.map((item) => ({
            ...item,
            applicableGoods: (item.applicableGoods as unknown as string).split(',') || [],
          }));

          return {
            type: 'ticket',
            data: couponList,
            availabled,
            used: undefined,
            hasCoupon: true,
            isRequestEnd: true,
            extends: {
              type: 'NEW_USER_EXCLUSIVE',
            },
          };
        }
        // 可用的优惠券
        const availabled = couponList
          .filter((item) => item.status === 1 && item.isValidityPeriod)
          .map((item) => ({
            ...item,
            applicableGoods: (item.applicableGoods as unknown as string).split(',') || [],
          }));
        // // 已用的优惠券
        const used = couponList
          .filter((item) => item.status === 2)
          .map((item) => ({
            ...item,
            applicableGoods: (item.applicableGoods as unknown as string).split(',') || [],
          }));

        return {
          type: 'ticket',
          data: couponList,
          availabled: availabled.length ? availabled : undefined,
          used: used.length ? used : undefined,
          hasCoupon: availabled.length ? true : false,
          isRequestEnd: true,
        };
      },

      getCouponInfo: async () => {
        try {
          const distributionInfo = await get().fetchDistributionInfo();

          if (distributionInfo) {
            // 分销逻辑处理
            const { discountMode, commodityDiscount } = distributionInfo;
            if (discountMode !== Mode.PRODUCT_MODE) {
              // 全商品优惠
              set({
                couponInfo: {
                  type: 'distribution_all',
                  data: distributionInfo,
                  hasCoupon: true,
                  isRequestEnd: true,
                },
              });
            } else {
              // 特定商品优惠

              let couponInfo: any;
              try {
                couponInfo = {
                  type: 'distribution_specific',
                  data: {
                    ...distributionInfo,
                    commodityDiscount: JSON.parse(commodityDiscount as unknown as string),
                  },
                  hasCoupon: true,
                  isRequestEnd: true,
                };
              } catch (error) {
                console.log('json.parse错误', error);
                couponInfo = {
                  type: 'distribution_specific',
                  data: {
                    ...distributionInfo,
                    commodityDiscount: [],
                  },
                  hasCoupon: true,
                  isRequestEnd: true,
                };
              }

              set({
                couponInfo,
              });
            }
          } else {
            const couponListInfo = await get().fetchCouponTicketInfo();
            set({ couponInfo: couponListInfo });
          }
        } catch (error) {
          console.error(error);
          set({
            couponInfo: {
              type: 'ticket',
              data: [],
              availabled: undefined,
              used: undefined,
              hasCoupon: false,
              isRequestEnd: true,
            },
          });
        } finally {
          return get().couponInfo;
        }
      },
      // 计算当前商品面板的折扣相关信息
      getCurrentDiscountInfo: (
        originPrice: number,
        commodityCode: string,
        couponInfoData: CouponInfo | undefined,
      ) => {
        let realPrice = originPrice;
        let hasCoupon = false;
        let discountType: DiscountType = 'none';
        let discountValue: number | undefined = undefined;
        const couponInfo = couponInfoData || get().couponInfo;
        if (!couponInfo?.hasCoupon) {
          return {
            hasCoupon,
            discountType,
            discountValue,
            realPrice,
            originPrice,
            couponSourceType: couponInfo.type, // 优惠来源
            couponDetail: couponInfo,
          };
        }
        try {
          switch (couponInfo.type) {
            case 'ticket': {
              const availabled = couponInfo.availabled;
              if (availabled?.length) {
                availabled.forEach((item) => {
                  if (item?.applicableGoods.includes(commodityCode)) {
                    if (item.content !== 1) {
                      hasCoupon = true;
                    }
                    discountType = item.type === 1 ? 'discount' : 'reduce';
                    discountValue = item.content;

                    realPrice = get().applyCouponDiscount(
                      originPrice * 100,
                      discountType,
                      discountValue,
                    );
                  }
                });
              }

              // if (
              //   availabled?.applicableGoods.includes(commodityCode) &&
              //   availabled?.content
              // ) {
              //   hasCoupon = true;
              //   discountType = availabled.type === 1 ? 'discount' : 'reduce';
              //   discountValue = availabled.content;

              //   realPrice = get().applyCouponDiscount(
              //     originPrice * 100,
              //     discountType,
              //     discountValue,
              //   );
              // }
              break;
            }

            case 'distribution_specific': {
              const target = couponInfo.data.commodityDiscount?.find(
                (item) => item.commodityCode === commodityCode,
              );
              if (target) {
                hasCoupon = true;
                discountType =
                  Number(target.discountMode) === Mode.FIXED_AMOUNT
                    ? 'reduce'
                    : 'discount';
                discountValue =
                  Number(target.discountMode) === Mode.FIXED_AMOUNT
                    ? target.discountAmount
                    : target.discount;

                realPrice = get().applyCouponDiscount(
                  originPrice * 100,
                  discountType,
                  discountValue,
                );
              }
              break;
            }

            case 'distribution_all': {
              hasCoupon = true;
              discountType =
                couponInfo.data.discountMode === Mode.FIXED_AMOUNT
                  ? 'reduce'
                  : 'discount';
              discountValue =
                couponInfo.data.discountMode === Mode.FIXED_AMOUNT
                  ? couponInfo.data.discountAmount
                  : couponInfo.data.discount;

              realPrice = get().applyCouponDiscount(
                originPrice * 100,
                discountType,
                discountValue,
              );
              console.log(realPrice);
              break;
            }

            default:
              break;
          }
          return {
            hasCoupon,
            discountType, // 优惠类型
            discountValue, // 优惠值
            realPrice, // 最终价格
            originPrice, // 原始价格
            couponSourceType: couponInfo.type, // 优惠来源
            couponDetail: couponInfo,
          };
        } catch (error) {
          console.error(error);
          return {
            hasCoupon: false,
            discountType: 'none',
            discountValue: 0,
            realPrice,
            originPrice,
            couponSourceType: couponInfo.type, // 优惠来源
            couponDetail: couponInfo,
          };
        }
      },

      applyCouponDiscount: (
        priceInCents: number, // 原价
        type: 'discount' | 'reduce',
        value?: number, // 折扣
      ) => {
        if (!value) return priceInCents / 100;
        let discountedPrice = priceInCents;
        if (type === 'discount') {
          discountedPrice = new Decimal(priceInCents)
            .times(value)
            .dividedBy(100)
            .toDecimalPlaces(2)
            .toNumber();
        } else if (type === 'reduce') {
          // 立减
          discountedPrice = (priceInCents * 100 - value * 100) / 10000;
        }
        return Math.max(discountedPrice, 0);
      },

      newUserCountDownTime: 0,
      setNewUserCountDownTime: (time) => {
        set((state) => {
          const newTime =
            typeof time === 'function' ? time(state.newUserCountDownTime) : time;
          return { newUserCountDownTime: newTime };
        });
      },
      showNewUserCountDownBaner: false,
      setShowNewUserCountDownBaner: (value) => {
        set({ showNewUserCountDownBaner: value });
      },

      userSalt: null,
      setUserSalt: (value: string | null) => {
        set({ userSalt: value });
      },
      getLoginState: () => {
        const user = getCookie('user');
        return !!user?.token;
      },
    }),
    {
      name: 'user_benefit_store',
      enabled: baseConfig.debuggable,
    },
  ),
  shallow,
);
export default UserStore;
